import { useMemo } from 'react'
import { useQuery, type UseQueryResult } from 'react-query'
import { useAuth } from '../../modules/auth'
import {
  getAccessibleVesselsForMe,
  type AssignableVessel,
} from '../../modules/Management/core/_requests'
import type { Vessel as ManagementVessel } from '../../modules/Management/core/_models'

export const ACCESSIBLE_VESSELS_QUERY_KEY = ['accessible-vessels'] as const
export const NO_ACCESSIBLE_VESSELS_MESSAGE = 'No vessels are available for your current access scope.'
export const ACCESSIBLE_VESSEL_FORBIDDEN_MESSAGE = 'You do not have access to this vessel or record.'

type CurrentUserLike = Record<string, any> | null | undefined

function resolveAccessibleVesselsQueryKey(currentUser: CurrentUserLike) {
  const role = currentUser?.role as Record<string, any> | undefined
  const vessel = currentUser?.vessel as Record<string, any> | undefined
  const companyGroupAdmin = currentUser?.companyGroupAdmin as Record<string, any> | undefined
  const companyAdmin = currentUser?.companyAdmin as Record<string, any> | undefined
  return [
    ...ACCESSIBLE_VESSELS_QUERY_KEY,
    currentUser?.id ?? currentUser?.user?.id ?? currentUser?.uid?.id ?? null,
    currentUser?.roleId ?? role?.id ?? role?.roleType ?? role?.roleName ?? role?.effectiveBehaviorType ?? null,
    currentUser?.companyGroupAdminId ?? companyGroupAdmin?.id ?? null,
    currentUser?.companyAdminId ?? companyAdmin?.id ?? null,
    currentUser?.vesselId ?? vessel?.id ?? null,
  ]
}

/**
 * Reusable current-user vessel lookup backed by the backend vessel-scope layer.
 * This is a data-loading helper only; backend RBAC and vessel-scope enforcement
 * remain authoritative.
 */
export const fetchAccessibleVesselsForCurrentUser = async (): Promise<AssignableVessel[]> => {
  return getAccessibleVesselsForMe()
}

export function useAccessibleVessels(): UseQueryResult<AssignableVessel[], Error> {
  const { currentUser } = useAuth()
  const queryKey = useMemo(
    () => resolveAccessibleVesselsQueryKey(currentUser as CurrentUserLike),
    [currentUser]
  )

  return useQuery<AssignableVessel[], Error>(
    queryKey,
    fetchAccessibleVesselsForCurrentUser,
    {
      staleTime: 0,
      refetchOnMount: 'always',
      retry: false,
    }
  )
}

export function normalizeAccessibleVessels(
  vessels: AssignableVessel[]
): ManagementVessel[] {
  return vessels.map(
    (vessel) =>
      ({
        id: vessel.id,
        name: vessel.name,
        fleet_name: vessel.name,
        imoNumber: vessel.imoNumber ?? '',
        vesselType: '',
        active: true,
        companyAdmin: {
          id: vessel.companyAdminId ?? 0,
        },
        companyGroupAdmin: vessel.companyGroupAdminId
          ? {
              id: vessel.companyGroupAdminId,
            }
          : undefined,
        companyGroupAdminId: vessel.companyGroupAdminId ?? null,
        companyGroupId: vessel.companyGroupAdminId ?? null,
        companyAdminId: vessel.companyAdminId ?? null,
        companyId: vessel.companyAdminId ?? null,
      } as unknown as ManagementVessel)
  )
}

export function formatAccessibleVesselError(error: unknown, fallback: string): string {
  const status = (error as { response?: { status?: number } })?.response?.status
  if (status === 403) {
    return ACCESSIBLE_VESSEL_FORBIDDEN_MESSAGE
  }
  const message =
    (error as { response?: { data?: { message?: string } } })?.response?.data?.message ||
    (error as Error)?.message ||
    ''
  return message || fallback
}
